import type { SnapColorMode, SnapLauncherPlacement, SnapThemeOptions } from '../types';

const SAFE_Z_MIN = 1000;
const SAFE_Z_MAX = 2147483000;

export interface ResolvedTheme {
  accentColor: string;
  foreground: string;
  background: string;
  borderRadius: string;
  launcherPosition: SnapLauncherPlacement;
  density: 'comfortable' | 'compact';
  colorMode: SnapColorMode;
  zIndex: number;
}

export function resolveTheme(input?: SnapThemeOptions): ResolvedTheme {
  const z = input?.zIndex ?? 2147000000;
  return {
    accentColor: sanitizeColor(input?.accentColor, '#0b6e4f'),
    foreground: sanitizeColor(input?.foreground, '#12231c'),
    background: sanitizeColor(input?.background, '#f7faf8'),
    borderRadius: sanitizeRadius(input?.borderRadius, '12px'),
    launcherPosition: normalizePlacement(input?.launcherPosition),
    density: input?.density === 'compact' ? 'compact' : 'comfortable',
    colorMode: input?.colorMode === 'dark' || input?.colorMode === 'system'
      ? input.colorMode
      : 'light',
    zIndex: Math.min(SAFE_Z_MAX, Math.max(SAFE_Z_MIN, z))
  };
}

function sanitizeColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  const v = value.trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v)) return v;
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/.test(v)) {
    return v;
  }
  return fallback;
}

function sanitizeRadius(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  if (/^\d+(\.\d+)?(px|rem)$/.test(value.trim())) return value.trim();
  return fallback;
}

function normalizePlacement(value?: string): SnapLauncherPlacement {
  switch (value) {
    case 'bottom-left':
    case 'top-right':
    case 'top-left':
    case 'bottom-right':
      return value;
    default:
      return 'bottom-right';
  }
}

export function applyThemeVars(root: HTMLElement, theme: ResolvedTheme): void {
  root.style.setProperty('--nv-snap-accent', theme.accentColor);
  root.style.setProperty('--nv-snap-fg', theme.foreground);
  root.style.setProperty('--nv-snap-bg', theme.background);
  root.style.setProperty('--nv-snap-radius', theme.borderRadius);
  root.style.setProperty('--nv-snap-z', String(theme.zIndex));
  root.dataset.placement = theme.launcherPosition;
  root.dataset.density = theme.density;
  root.dataset.colorMode = theme.colorMode;
}
